import { buildMatchingContext } from "./matchingContext";
import { evaluateRule } from "./ruleEvaluator";
import { selectedLabProgramIds } from "./selectedProgramIds";

const isPopulatedRule = (rule) => (
  rule
  && typeof rule === "object"
  && !Array.isArray(rule)
  && Object.keys(rule).length > 0
);

export const programLabIdentity = (lab) => {
  if (!lab || typeof lab !== "object") return "";
  const requirementKind = String(lab.requirement_kind || "").toLowerCase();
  const isCombined = requirementKind === "combined"
    || (requirementKind !== "single" && Boolean(
      lab.source_combined_panel_id || lab.combined_panel_id,
    ));
  const identifier = isCombined
    ? (lab.source_combined_panel_id || lab.combined_panel_id)
    : (lab.source_panel_id || lab.panel_id);
  const fallback = identifier || lab.assignment_id || lab.requirement_id;
  return fallback ? `${isCombined ? "combined" : "single"}:${fallback}` : "";
};

const deduplicateCustomProgramLabs = (treatments) => {
  const seen = new Map();
  return treatments.map((program) => {
    const labs = [];
    (program.labs || []).forEach((lab) => {
      const identity = programLabIdentity(lab);
      if (!identity || !seen.has(identity)) {
        const projected = {
          ...lab,
          ...(identity ? { canonical_lab_key: identity } : {}),
          contributing_requirement_ids: Array.from(new Set([
            ...(lab.requirement_id ? [String(lab.requirement_id)] : []),
            ...(lab.contributing_requirement_ids || []).map(String),
          ])),
          contributing_assignment_ids: Array.from(new Set([
            ...(lab.assignment_id ? [String(lab.assignment_id)] : []),
            ...(lab.contributing_assignment_ids || []).map(String),
          ])),
        };
        if (identity) seen.set(identity, projected);
        labs.push(projected);
        return;
      }

      const canonical = seen.get(identity);
      const requirementIds = [
        ...(lab.requirement_id ? [lab.requirement_id] : []),
        ...(lab.contributing_requirement_ids || []),
      ];
      requirementIds.forEach((value) => {
        const requirementId = String(value);
        if (!canonical.contributing_requirement_ids.includes(requirementId)) {
          canonical.contributing_requirement_ids.push(requirementId);
        }
      });
      const assignmentIds = [
        ...(lab.assignment_id ? [lab.assignment_id] : []),
        ...(lab.contributing_assignment_ids || []),
      ];
      assignmentIds.forEach((value) => {
        const assignmentId = String(value);
        if (!canonical.contributing_assignment_ids.includes(assignmentId)) {
          canonical.contributing_assignment_ids.push(assignmentId);
        }
      });
      canonical.is_required = canonical.is_required !== false
        || lab.is_required !== false;
      canonical.is_release_required = canonical.is_release_required !== false
        || lab.is_release_required !== false;
    });
    return { ...program, labs };
  });
};

/**
 * Admin payloads can include an empty legacy `visibility_rule` alongside the
 * configured plural/camel-case field. Prefer an actual configured rule over
 * that empty compatibility placeholder.
 */
export const labVisibilityRule = (lab) => {
  const candidates = [
    lab?.visibility_rules,
    lab?.visibilityRules,
    lab?.visibility_rule,
    lab?.conditional_logic,
  ];
  return candidates.find(isPopulatedRule)
    || candidates.find((rule) => rule !== undefined && rule !== null)
    || {};
};

export const visibleProgramLabCheckout = (
  question,
  questionnaire,
  responses,
  { customProgram = false, previewOnly = false } = {},
) => {
  const selectedPrograms = selectedLabProgramIds(questionnaire, responses);
  const treatments = (question?.program_labs || [])
      .filter((program) => (
        selectedPrograms.size === 0
        || selectedPrograms.has(String(program.program_id))
      ))
      .map((program) => {
        // Program-scoped answers intentionally expose their source aliases
        // only to the same Program. Lab rules are program-scoped too.
        const context = buildMatchingContext(
          questionnaire,
          responses,
          { program_id: program.program_id },
        );
        return {
          program_id: String(program.program_id),
          program_name: program.program_name,
          labs: (program.labs || []).filter((lab) => {
            if (question?.runtime_authoritative_labs === true) return true;
            const rule = labVisibilityRule(lab);
            return !Object.keys(rule).length || evaluateRule(rule, context);
          }),
        };
      });
  return {
    treatments: customProgram && previewOnly
      ? deduplicateCustomProgramLabs(treatments)
      : treatments,
  };
};

export const hasVisibleProgramLabs = (
  question,
  questionnaire,
  responses,
  options,
) => (
  visibleProgramLabCheckout(question, questionnaire, responses, options)
    .treatments
    .some((program) => program.labs.length > 0)
);
